import {
    Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, Popover, TextField, Grid, CircularProgress
} from '@mui/material'
import { CalendarMonth } from '@mui/icons-material'
import { FC, useEffect, useState } from 'react'
import { Api, numberWithCommas } from '../../helpers'
import { getCurrency } from '../../helpers/countries'
import { Connect, mapStateToProps } from '../../Redux'

interface CountryRevenue {
    _id: string
    country: string
    earning: number
    Username: string
}

interface DailyRevenue {
    tableId: string
    date: string
    totalAmount: number
    totalGames: number
    owner: string
    country: string
}

interface StateProps {
    countryRevenue: CountryRevenue[]
    dailyRevenue: DailyRevenue[]
    loadingCountry: boolean
    loadingDaily: boolean
    startDate: string
    endDate: string
    calendarAnchor: HTMLElement | null
}

const today = () => new Date().toISOString().split('T')[0]

const formatLabel = (start: string, end: string) => {
    const fmt = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    return start === end ? fmt(start) : `${fmt(start)} – ${fmt(end)}`
}

const Revenue: FC = () => {
    const [state, setState] = useState<StateProps>({
        countryRevenue: [],
        dailyRevenue: [],
        loadingCountry: true,
        loadingDaily: true,
        startDate: today(),
        endDate: today(),
        calendarAnchor: null
    })
    const { countryRevenue, dailyRevenue, loadingCountry, loadingDaily, startDate, endDate, calendarAnchor } = state

    const getCountryRevenue = () => {
        setState(s => ({ ...s, loadingCountry: true }))
        Api({
            controller: 'Payments',
            action: 'getRevenueByCountry'
        })
            .then((resp: any) => {
                setState(s => ({ ...s, countryRevenue: resp, loadingCountry: false }))
            })
            .catch(() => setState(s => ({ ...s, loadingCountry: false })))
    }

    const getDailyRevenue = ({ start, end }: { start?: string; end?: string } = {}) => {
        setState(s => ({ ...s, loadingDaily: true }))
        Api({
            controller: 'Payments',
            action: 'getDailyRevenue',
            startDate: start ?? startDate,
            endDate: end ?? endDate
        })
            .then((resp: any) => {
                setState(s => ({ ...s, dailyRevenue: resp, loadingDaily: false }))
            })
            .catch(() => setState(s => ({ ...s, loadingDaily: false })))
    }

    useEffect(() => {
        getCountryRevenue()
        getDailyRevenue({ start: today(), end: today() })
    }, [])

    return (
        <Box>
            <Box sx={{ padding: 2, textAlign: 'left' }}>
                <Typography sx={{ color: '#000', fontSize: 25, fontWeight: 'bold' }}>Revenue</Typography>
            </Box>

            <Grid container spacing={2} sx={{ px: '5px' }}>
                {/* Left — Revenue per Country */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ bgcolor: 'primary.main', px: 2, py: 1.5 }}>
                            <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: 15 }}>Revenue by Country</Typography>
                        </Box>
                        {loadingCountry ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : countryRevenue.length === 0 ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <Typography sx={{ color: '#727A8A', fontSize: 14 }}>No data available</Typography>
                            </Box>
                        ) : (
                            <Table size='small'>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Username</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Country</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align='right'>Earning</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {countryRevenue.map(({ _id, Username, country, earning }) => (
                                        <TableRow key={_id} hover>
                                            <TableCell>{Username}</TableCell>
                                            <TableCell>{country}</TableCell>
                                            <TableCell align='right'>{getCurrency(country)} {numberWithCommas(earning)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Paper>
                </Grid>

                {/* Right — Daily Revenue */}
                <Grid item xs={12} md={7}>
                    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                        <Box sx={{ bgcolor: 'primary.main', px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: 15 }}>Daily Revenue</Typography>
                            <Button
                                size='small'
                                variant='outlined'
                                startIcon={<CalendarMonth sx={{ color: 'white' }} />}
                                sx={{ borderColor: 'white', color: 'white', borderRadius: 2, whiteSpace: 'nowrap', '&:hover': { borderColor: 'white' } }}
                                onClick={e => setState(s => ({ ...s, calendarAnchor: e.currentTarget }))}
                            >
                                {formatLabel(startDate, endDate)}
                            </Button>
                        </Box>
                        {loadingDaily ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress size={28} />
                            </Box>
                        ) : dailyRevenue.length === 0 ? (
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <Typography sx={{ color: '#727A8A', fontSize: 14 }}>No data for this period</Typography>
                            </Box>
                        ) : (
                            <Table size='small'>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Owner</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align='right'>Games</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }} align='right'>Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dailyRevenue.map(({ tableId, date, owner, totalGames, totalAmount, country }) => (
                                        <TableRow key={tableId} hover>
                                            <TableCell>{new Date(date + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                                            <TableCell>{owner}</TableCell>
                                            <TableCell align='right'>{totalGames}</TableCell>
                                            <TableCell align='right'>{getCurrency(country)} {numberWithCommas(totalAmount)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            <Popover
                open={Boolean(calendarAnchor)}
                anchorEl={calendarAnchor}
                onClose={() => setState(s => ({ ...s, calendarAnchor: null }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 260 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 15 }}>Date Range</Typography>
                    <TextField
                        size='small'
                        type='date'
                        label='From'
                        value={startDate}
                        onChange={e => setState(s => ({ ...s, startDate: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                    <TextField
                        size='small'
                        type='date'
                        label='To'
                        value={endDate}
                        onChange={e => {
                            const newEnd = e.target.value
                            setState(s => ({ ...s, endDate: newEnd, calendarAnchor: null }))
                            getDailyRevenue({ start: startDate, end: newEnd })
                        }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                    />
                </Box>
            </Popover>
        </Box>
    )
}

export default Connect(mapStateToProps)(Revenue)
